import { useMemo, useState } from "react";
import { useBreakingNews } from "@/hooks/content";
import {
  PageHeader,
  StatusBadge,
  ConfirmDialog,
  Modal,
  useToast,
  newId,
} from "./components/admin-ui";
import type { BreakingItem } from "@/mocks/cms";

type FormState = Pick<BreakingItem, "headline" | "link" | "priority" | "status">;

const emptyForm: FormState = { headline: "", link: "", priority: "medium", status: "active" };

const priorityBadge: Record<BreakingItem["priority"], string> = {
  high: "bg-accent-100 text-accent-700",
  medium: "bg-yellow-100 text-yellow-700",
  low: "bg-secondary-100 text-secondary-700",
};

export default function BreakingNewsAdminPage() {
  const { items, add, update, remove } = useBreakingNews();
  const { show } = useToast();

  const [query, setQuery] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<BreakingItem | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((it) => !q || it.headline.toLowerCase().includes(q));
  }, [items, query]);

  const activeCount = items.filter((i) => i.status === "active").length;

  function openCreate() {
    setEditing(null);
    setForm(emptyForm);
    setModalOpen(true);
  }

  function openEdit(item: BreakingItem) {
    setEditing(item);
    setForm({
      headline: item.headline,
      link: item.link,
      priority: item.priority,
      status: item.status,
    });
    setModalOpen(true);
  }

  function save() {
    if (!form.headline.trim()) {
      show("Please enter a headline.", "error");
      return;
    }
    if (editing) {
      update(editing.id, form);
      show("Saved successfully.");
    } else {
      add({ id: newId(), ...form });
      show("Added to ticker.");
    }
    setModalOpen(false);
  }

  function toggleStatus(item: BreakingItem) {
    update(item.id, { status: item.status === "active" ? "inactive" : "active" });
    show(item.status === "active" ? "Deactivated." : "Activated.");
  }

  const confirmItem = items.find((i) => i.id === confirmId) ?? null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Breaking News"
        subtitle="Manage the scrolling ticker on the homepage. Active items appear for all viewers immediately."
        actions={
          <button
            type="button"
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-add-line text-sm" />
            </span>
            New Breaking Item
          </button>
        }
      />

      <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-4 flex items-start gap-3">
        <span className="w-9 h-9 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-700 shrink-0">
          <i className="ri-information-line text-base" />
        </span>
        <div className="text-sm text-foreground-700">
          <p className="font-semibold text-foreground-900">How the ticker works</p>
          <p className="mt-0.5">
            {activeCount > 0
              ? `${activeCount} active item${activeCount === 1 ? " is" : "s are"} showing in the homepage marquee right now.`
              : "No active items yet — the marquee is automatically showing your latest published news headlines instead."}{" "}
            Set an item's status to <span className="font-medium">Active</span> to feature it in the marquee.
          </p>
        </div>
      </div>

      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400">
          <i className="ri-search-line text-sm" />
        </span>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search headlines…"
          className="w-full pl-9 pr-3 py-2 bg-white border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-lg border border-background-200 p-10 text-center">
          <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-flashlight-line text-xl" />
          </div>
          <p className="mt-3 text-sm text-foreground-600">No breaking items yet. Add one to feature it in the marquee.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-background-200 divide-y divide-background-50">
          {filtered.map((item) => (
            <div key={item.id} className="flex items-start gap-4 px-4 py-4">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge tone={item.status === "active" ? "live" : "draft"}>
                    {item.status === "active" ? "Active" : "Inactive"}
                  </StatusBadge>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold ${priorityBadge[item.priority]}`}>
                    {item.priority}
                  </span>
                </div>
                <h3 className="mt-1.5 text-sm md:text-base font-semibold text-foreground-950 leading-snug">
                  {item.headline}
                </h3>
                {item.link && (
                  <p className="mt-0.5 text-xs text-foreground-500 truncate">{item.link}</p>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  onClick={() => toggleStatus(item)}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium border rounded-md whitespace-nowrap hover:bg-background-50"
                  title={item.status === "active" ? "Deactivate" : "Activate"}
                >
                  <i className={`${item.status === "active" ? "ri-pause-circle-line" : "ri-play-circle-line"} text-sm`} />
                  {item.status === "active" ? "Deactivate" : "Activate"}
                </button>
                <button
                  type="button"
                  onClick={() => openEdit(item)}
                  className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 text-foreground-600"
                  aria-label="Edit"
                >
                  <i className="ri-pencil-line text-base" />
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmId(item.id)}
                  className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-accent-50 text-accent-600"
                  aria-label="Delete"
                >
                  <i className="ri-delete-bin-line text-base" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? "Edit Breaking Item" : "New Breaking Item"}
        subtitle="Active items scroll in the homepage marquee for all viewers."
        footer={
          <>
            <button
              type="button"
              onClick={() => setModalOpen(false)}
              className="px-4 py-2 text-sm font-medium text-foreground-700 border border-background-200 rounded-md hover:bg-background-50 whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 whitespace-nowrap"
            >
              Save
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground-800 mb-1.5">
              Headline <span className="text-accent-500">*</span>
            </label>
            <input
              type="text"
              value={form.headline}
              onChange={(e) => setForm({ ...form, headline: e.target.value })}
              placeholder="e.g. BREAKING: L+TV announces new program"
              className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground-800 mb-1.5">Link (optional)</label>
            <input
              type="text"
              value={form.link}
              onChange={(e) => setForm({ ...form, link: e.target.value })}
              placeholder="/news/your-article-slug"
              className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground-800 mb-1.5">Priority</label>
              <select
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value as BreakingItem["priority"] })}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
              >
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-800 mb-1.5">Status</label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value as BreakingItem["status"] })}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={confirmId !== null}
        title="Delete breaking item?"
        message={
          confirmItem
            ? `Are you sure you want to permanently delete "${confirmItem.headline}"? This action cannot be undone.`
            : ""
        }
        onConfirm={() => {
          if (confirmId) {
            remove(confirmId);
            show("Deleted successfully.");
          }
          setConfirmId(null);
        }}
        onCancel={() => setConfirmId(null)}
      />
    </div>
  );
}